import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { trackViewContent } from "../utils/metaPixel";
import { 
  Phone, 
  Check, 
  Shield, 
  Clock, 
  DollarSign,
  Home,
  FileText,
  Handshake,
  ChevronDown,
  ChevronUp,
  Star,
  Quote,
  Users,
  ArrowRight,
  Trees
} from "lucide-react";

// Assets - using local paths for reliable loading
const KI_LOGO = "/images/ki-logo.png";
const OWNER_PHOTO = "/images/rickey-knotts.jpg";

const LandingPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);

  // Track ViewContent when landing page loads (runs once on mount)
  useEffect(() => {
    trackViewContent({
      content_name: 'Homepage',
      content_category: 'Landing Page'
    });
  }, []);

  const benefits = [
    { icon: Check, text: "Sell As-Is — No Repairs Needed" },
    { icon: DollarSign, text: "No Commissions or Fees" },
    { icon: Clock, text: "Close On Your Timeline" },
    { icon: Handshake, text: "Fast & Simple Process" },
    { icon: Shield, text: "Local Trusted Buyers" },
    { icon: Users, text: "We help homeowners avoid agents, repairs, and delays" }
  ];

  const faqs = [
    {
      question: "How quickly can you close?",
      answer: "We can close in as little as 7-14 days, or on your timeline. We work around your schedule."
    },
    {
      question: "Do you buy houses in any condition?",
      answer: "Yes! We buy houses as-is. No repairs, cleaning, or renovations needed. We handle everything."
    },
    {
      question: "Are there any obligations or fees?",
      answer: "Absolutely not. Our cash offer is 100% free with no obligations. No commissions, no hidden fees."
    }
  ];

  const steps = [
    { number: "1", title: "Contact Us", description: "Fill out our simple form or give us a call", icon: Phone },
    { number: "2", title: "Get a Fair Cash Offer", description: "No obligation — just a fair offer", icon: FileText },
    { number: "3", title: "Close Fast & Get Paid", description: "Close on your timeline and get your cash", icon: DollarSign }
  ];

  const testimonials = [
    {
      name: "Sarah M.",
      location: "Charlotte, NC",
      text: "Knotts Investments made the process easy and stress-free. I didn't have to make any repairs and closed quickly."
    },
    {
      name: "James T.",
      location: "Raleigh, NC",
      text: "I was in a tough situation and needed to sell fast. They gave me a fair cash offer and closed in just 10 days!"
    },
    {
      name: "Linda K.",
      location: "Greensboro, NC",
      text: "Professional, honest, and quick. They handled everything and I didn't pay a single fee. Highly recommend!"
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero Section - Centered */}
      <section className="hero-section min-h-screen relative flex items-center justify-center" data-testid="hero-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img 
              src={KI_LOGO} 
              alt="Knotts Investments LLC" 
              className="h-24 sm:h-28 lg:h-36 w-auto logo-hover"
              data-testid="ki-logo"
            />
          </div>
          
          {/* Company Name */}
          <h2 className="text-[#D4AF37] text-lg sm:text-xl font-semibold tracking-widest mb-8">
            Knotts Investments LLC
          </h2>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-4" data-testid="hero-headline">
            Sell Your House or Land Fast in{" "}
            <span className="gold-text">North Carolina</span> and <span className="gold-text">Nationwide</span>
          </h1>
          
          {/* Subheadline */}
          <p className="text-xl sm:text-2xl lg:text-3xl text-[#D4AF37] font-semibold mb-6">
            North Carolina-Based. Trusted Nationwide.
          </p>
          
          {/* Supporting Text */}
          <p className="text-lg text-[#A3A3A3] max-w-2xl mx-auto mb-8 leading-relaxed">
            Whether you&apos;re selling a house, vacant land, or commercial property in North Carolina, Texas, Florida, Georgia, South Carolina, or anywhere else in the United States, we can help you sell quickly with a fair cash offer.
          </p>

          {/* Benefits Line */}
          <p className="text-white text-lg sm:text-xl font-medium mb-10">
            No Repairs <span className="text-[#D4AF37]">•</span> No Commissions <span className="text-[#D4AF37]">•</span> No Closing Costs
          </p>

          {/* Main CTA Button */}
          <div className="mb-8">
            <Link 
              to="/sell-my-house"
              className="btn-gold px-12 py-5 text-xl font-bold inline-block"
              data-testid="hero-cta"
            >
              GET MY CASH OFFER
            </Link>
          </div>
          
          {/* Phone Number */}
          <a 
            href="tel:704-930-0403" 
            className="inline-flex items-center gap-3 text-[#D4AF37] text-xl sm:text-2xl font-bold hover:text-[#E5C158] transition-colors"
            data-testid="hero-phone"
          >
            <Phone className="w-6 h-6" />
            Call or Text: 704-930-0403
          </a>
        </div>
      </section>

      {/* Trust Bar */}
      <div className="trust-bar py-4 px-6 flex flex-wrap justify-center gap-4 sm:gap-8 text-sm sm:text-base">
        <span className="flex items-center gap-2 text-[#A3A3A3]">
          <Shield className="w-4 h-4 text-[#D4AF37]" />
          NC-Based
        </span>
        <span className="flex items-center gap-2 text-[#A3A3A3]">
          <Home className="w-4 h-4 text-[#D4AF37]" />
          We Buy Houses
        </span>
        <span className="flex items-center gap-2 text-[#A3A3A3]">
          <Trees className="w-4 h-4 text-[#D4AF37]" />
          We Buy Land
        </span>
        <span className="flex items-center gap-2 text-[#D4AF37] font-semibold">
          <Handshake className="w-4 h-4" />
          Investing In Your Tomorrow
        </span>
      </div>

      {/* Who We Are Section */}
      <section className="section-light py-16 lg:py-24" data-testid="who-we-are-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12 lg:mb-16">
            Who We Are
          </h2>
          
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Owner Photo */}
            <div className="flex justify-center lg:justify-end">
              <div className="relative">
                <img 
                  src={OWNER_PHOTO} 
                  alt="Rickey Knotts - Owner"
                  className="w-64 h-64 sm:w-80 sm:h-80 object-cover owner-image"
                  data-testid="owner-photo"
                />
                <div className="absolute -bottom-4 -right-4 bg-[#D4AF37] text-black px-4 py-2 font-bold text-sm">
                  RICKEY KNOTTS
                </div>
              </div>
            </div>

            {/* Text Content */}
            <div className="text-center lg:text-left">
              <p className="text-xl text-[#D4AF37] font-semibold mb-4 flex items-center justify-center lg:justify-start gap-2">
                <Home className="w-5 h-5" />
                Headquartered in Charlotte, NC — Serving Homeowners Nationwide
              </p>
              <p className="text-lg text-[#A3A3A3] leading-relaxed mb-8">
                At <span className="text-[#D4AF37] font-semibold">Knotts Investments LLC</span>, we help 
                homeowners across the United States sell quickly without stress. We provide fair cash offers, handle the hard work, 
                and make the process simple — no matter where your property is located.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span className="text-white text-lg">
                    <strong>Sell As-Is</strong> — No Repairs Needed
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span className="text-white text-lg">
                    <strong>No Commissions</strong> or Fees
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <Check className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <span className="text-white text-lg">
                    <strong>Close On Your Timeline</strong>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Investing In Your Tomorrow Teaser */}
      <section className="py-16 lg:py-20 bg-[#0A0A0A] border-y border-[#D4AF37]/20" data-testid="investing-teaser">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4">Our Philosophy</p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            More Than Buying Houses
          </h2>
          <p className="text-lg text-[#A3A3A3] mb-8 max-w-2xl mx-auto leading-relaxed">
            At Knotts Investments, we believe every homeowner deserves options, respect, 
            and a path forward. We don&apos;t just buy properties—we invest in people.
          </p>
          <Link 
            to="/why-knotts"
            className="inline-flex items-center gap-2 text-[#D4AF37] text-lg font-semibold hover:text-[#E5C158] transition-colors group"
            data-testid="investing-teaser-link"
          >
            Learn what &ldquo;Investing In Your Tomorrow&rdquo; really means
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section-dark py-16 lg:py-24" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-4">
            How It Works
          </h2>
          <p className="text-[#A3A3A3] text-center text-lg mb-12 lg:mb-16">
            Three simple steps to sell your house fast
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step) => (
              <div 
                key={`step-${step.number}`}
                className="relative text-center p-8 benefit-card"
                data-testid={`step-${step.number}`}
              >
                <div className="step-number">{step.number}</div>
                <div className="relative z-10">
                  <div className="w-16 h-16 mx-auto mb-6 bg-[#D4AF37] flex items-center justify-center">
                    <step.icon className="w-8 h-8 text-black" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-[#A3A3A3]">{step.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <Link 
              to="/sell-my-house"
              className="btn-gold px-10 py-4 text-lg font-bold inline-block"
              data-testid="how-it-works-cta"
            >
              GET MY CASH OFFER
            </Link>
          </div>
        </div>
      </section>

      {/* We Buy Land Section */}
      <section className="section-light py-16 lg:py-24 border-t border-[#D4AF37]/20" data-testid="we-buy-land-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Content */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#D4AF37] flex items-center justify-center">
                  <Trees className="w-6 h-6 text-black" />
                </div>
                <h2 className="text-3xl sm:text-4xl font-bold text-white">
                  We Buy <span className="gold-text">Land</span> Too!
                </h2>
              </div>
              <p className="text-[#A3A3A3] text-lg mb-8 leading-relaxed">
                Have vacant land you need to sell? We buy all types of land nationwide - no listing fees, no commissions, fast closings.
              </p>
              
              <div className="grid grid-cols-2 gap-4 mb-8">
                <div className="flex items-center gap-2 text-[#A3A3A3]">
                  <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span>Vacant Lots</span>
                </div>
                <div className="flex items-center gap-2 text-[#A3A3A3]">
                  <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span>Inherited Land</span>
                </div>
                <div className="flex items-center gap-2 text-[#A3A3A3]">
                  <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span>Rural Acreage</span>
                </div>
                <div className="flex items-center gap-2 text-[#A3A3A3]">
                  <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span>Residential Lots</span>
                </div>
                <div className="flex items-center gap-2 text-[#A3A3A3]">
                  <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span>No Commissions</span>
                </div>
                <div className="flex items-center gap-2 text-[#A3A3A3]">
                  <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                  <span>Fast Closings</span>
                </div>
              </div>

              <Link 
                to="/sell-my-house"
                className="btn-gold px-8 py-4 text-lg font-bold inline-flex items-center gap-2 group"
                data-testid="land-cta"
              >
                Get a Cash Offer for Your Land
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>

            {/* Right side - Visual Card */}
            <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] border border-[#D4AF37]/30 p-8 lg:p-10">
              <div className="text-center">
                <Trees className="w-16 h-16 mx-auto text-[#D4AF37] mb-6" />
                <h3 className="text-2xl font-bold text-white mb-4">Selling Land?</h3>
                <p className="text-[#A3A3A3] mb-6">
                  Don&apos;t let your vacant land sit unused. Get a fair cash offer today - no obligations.
                </p>
                <div className="space-y-3 text-left max-w-xs mx-auto">
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#D4AF37] font-bold">1</span>
                    </div>
                    <span className="text-[#A3A3A3]">Tell us about your land</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#D4AF37] font-bold">2</span>
                    </div>
                    <span className="text-[#A3A3A3]">Receive a cash offer</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <div className="w-8 h-8 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#D4AF37] font-bold">3</span>
                    </div>
                    <span className="text-[#A3A3A3]">Close and get paid</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="section-light py-16 lg:py-24" data-testid="why-choose-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12 lg:mb-16">
            Why Choose <span className="gold-text">Knotts Investments?</span>
          </h2>

          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Benefits */}
            <div className="space-y-4">
              {benefits.map((benefit) => (
                <div 
                  key={`benefit-${benefit.text.substring(0, 15)}`}
                  className="benefit-card p-5 flex items-center gap-4"
                  data-testid={`benefit-${benefit.text.substring(0, 10)}`}
                >
                  <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <span className="text-white text-lg font-medium">{benefit.text}</span>
                </div>
              ))}
              
              <Link 
                to="/sell-my-house"
                className="btn-gold w-full py-4 mt-6 text-lg font-bold block text-center"
                data-testid="benefits-cta"
              >
                GET MY CASH OFFER
              </Link>
            </div>

            {/* FAQ */}
            <div className="space-y-4">
              {faqs.map((faq) => (
                <div 
                  key={`faq-${faq.question.substring(0, 20)}`}
                  className="faq-item p-5 cursor-pointer"
                  onClick={() => setExpandedFaq(expandedFaq === faq.question ? null : faq.question)}
                  data-testid={`faq-${faq.question.substring(0, 15)}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-[#D4AF37] flex-shrink-0" />
                      <span className="text-white font-medium">{faq.question}</span>
                    </div>
                    {expandedFaq === faq.question ? (
                      <ChevronUp className="w-5 h-5 text-[#D4AF37]" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-[#A3A3A3]" />
                    )}
                  </div>
                  {expandedFaq === faq.question && (
                    <p className="mt-4 pl-8 text-[#A3A3A3] animate-fade-in-up">
                      {faq.answer}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="section-dark py-16 lg:py-24" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center mb-4">
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star key={`header-star-${star}`} className="w-6 h-6 text-[#D4AF37] fill-[#D4AF37]" />
              ))}
            </div>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-12 lg:mb-16">
            What Homeowners Are Saying
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <div 
                key={`testimonial-${testimonial.name}`}
                className="benefit-card p-6 relative"
                data-testid={`testimonial-${testimonial.name}`}
              >
                <Quote className="w-10 h-10 text-[#D4AF37]/30 absolute top-4 right-4" />
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={`${testimonial.name}-star-${star}`} className="w-4 h-4 text-[#D4AF37] fill-[#D4AF37]" />
                  ))}
                </div>
                <p className="text-[#A3A3A3] italic mb-4 leading-relaxed">
                  &ldquo;{testimonial.text}&rdquo;
                </p>
                <div className="border-t border-white/10 pt-4">
                  <p className="text-white font-semibold">{testimonial.name}</p>
                  <p className="text-[#D4AF37] text-sm">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 lg:py-32 bg-[#0A0A0A] border-t border-[#D4AF37]/20" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Ready to Sell Your House?
          </h2>
          <p className="text-lg text-[#A3A3A3] mb-8 max-w-2xl mx-auto">
            Whether you&apos;re in North Carolina or anywhere else in the USA, we&apos;re ready to make you a fair cash offer.
          </p>
          <Link 
            to="/sell-my-house"
            className="btn-gold px-12 py-5 text-xl font-bold inline-block mb-8"
            data-testid="final-cta-button"
          >
            GET MY CASH OFFER
          </Link>
          <p className="text-xl text-[#A3A3A3]">
            Call or Text: <a href="tel:704-930-0403" className="text-[#D4AF37] font-bold hover:text-[#E5C158]">704-930-0403</a>
          </p>
          
          <p className="mt-12 text-[#D4AF37] text-lg font-semibold tracking-widest">
            — Investing In Your Tomorrow —
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-[#0A0A0A] border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img 
            src={KI_LOGO} 
            alt="Knotts Investments LLC" 
            className="h-12 w-auto mx-auto mb-4"
          />
          <div className="flex justify-center gap-4 text-sm text-[#A3A3A3] mb-4">
            <Link to="/privacy-policy" className="hover:text-[#D4AF37] transition-colors">Privacy Policy</Link>
            <span>|</span>
            <Link to="/terms-conditions" className="hover:text-[#D4AF37] transition-colors">Terms & Conditions</Link>
          </div>
          <p className="text-[#A3A3A3] text-sm">
            © {new Date().getFullYear()} Knotts Investments LLC. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Mobile Sticky CTA */}
      <div className="mobile-sticky-cta block md:hidden" data-testid="mobile-sticky-cta">
        <Link 
          to="/sell-my-house"
          className="btn-gold w-full py-4 text-lg font-bold block text-center"
        >
          GET MY CASH OFFER
        </Link>
      </div>
    </div>
  );
};

export default LandingPage;
